import { CheckOutlined, CopyOutlined } from '@ant-design/icons';
import { message } from 'antd';
import React, { useMemo, useState } from 'react';
import ShikiHighlighter from 'react-shiki';
import { useTheme } from '../../contexts/ThemeContext';
import './ShikiCodeBlock.css';

/**
 * Production-grade code block component using Shiki (VS Code-quality highlighting)
 */
const ShikiCodeBlock = ({
  code,
  language = 'text',
  showLineNumbers = true,
  className = '',
  inline = false,
  maxLines = null,
  showCopyButton = true
}) => {
  const { isDarkMode } = useTheme();
  const [copied, setCopied] = useState(false);

  const theme = isDarkMode ? 'material-theme-darker' : 'vitesse-light';

  const { displayCode, isTruncated, totalLines } = useMemo(() => {
    if (!maxLines) {
      return {
        displayCode: code,
        isTruncated: false,
        totalLines: code.split('\n').length
      };
    }

    const lines = code.split('\n');
    const total = lines.length;

    if (total <= maxLines) {
      return { displayCode: code, isTruncated: false, totalLines: total };
    }

    return {
      displayCode: lines.slice(0, maxLines).join('\n'),
      isTruncated: true,
      totalLines: total
    };
  }, [code, maxLines]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);

      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      message.error('Failed to copy code');
    }
  };

  if (inline) {
    return (
      <code className={`shiki-inline-code ${className}`}>
        {displayCode}
      </code>
    );
  }

  return (
    <div className={`shiki-code-block-wrapper ${className} ${isDarkMode ? 'dark' : 'light'}`}>
      {isTruncated && (
        <div className="shiki-truncation-notice">
          ⚠️ Showing first {maxLines} lines of {totalLines} total lines
        </div>
      )}

      <div className="shiki-code-block-container">
        {showCopyButton && (
          <button
            className={`shiki-copy-button ${copied ? 'copied' : ''}`}
            onClick={handleCopy}
            aria-label="Copy code"
          >
            {copied ? (
              <>
                <CheckOutlined />
              </>
            ) : (
              <>
                <CopyOutlined />
              </>
            )}
          </button>
        )}

        <ShikiHighlighter
          language={language}
          theme={theme}
          showLineNumbers={showLineNumbers}
          className="shiki-block"
        >
          {displayCode}
        </ShikiHighlighter>
      </div>
    </div>
  );
};

export default ShikiCodeBlock;
